'use strict';

/**
 * This care type is also known as "short-term (residential) care"
 *
 * @param constants
 * @param numberUtils
 * @param kirkleesCareAssessmentService
 * @returns {RespiteCareType}
 * @constructor
 */
function RespiteCareTypeFactory(constants, numberUtils, kirkleesCareAssessmentService, careAssessmentService) {

  function RespiteCareType(userInputModel) {

    this.getTotalWeeklyExpenses = function () {

      return ['rent', 'councilTax', 'mortgage', 'waterRates', 'homeContentsInsurance', 'groundRent'].reduce(
        function (runningTotal, expenseType) {
          return runningTotal + userInputModel.getWeeklyExpense(expenseType);
        }, 0);
    };

    this.getAllowableWorkingTaxCredit = function (workingTaxCreditIncome) {
      return userInputModel.isCouple() ? workingTaxCreditIncome / 2 : workingTaxCreditIncome;
    };

    this.getPartnerMinimumIncomeGuarantee = function (results) {
      var partnerUserType = constants.userTypes.partner;
      var plaTable = constants.personalLivingAllowance.partnerRespite;
      var partnerAgeLevel = results[partnerUserType].ageLevel;
      var partnerDisabilityLevel = kirkleesCareAssessmentService.getDisabilityLevel(userInputModel.getPartnerData());

      var partnerMIG = careAssessmentService.searchPlaTable(plaTable, partnerAgeLevel, partnerDisabilityLevel);
      partnerMIG -= this.getDisregardedPrivatePension();
      return Math.max(partnerMIG, 0);
    };

    this.personalLivingAllowance = function (results) {
      results[constants.userTypes.user].totalPLA = -1 * constants.personalLivingAllowance.residentialRate;
    };

    this.getDisregardedAttendanceAllowance = function () {
      return userInputModel.getApplicantData().getWeeklyIncome('attendanceAllowance') || 0;
    };

    this.getDisregardedPrivatePension = function () {
      var privatePensionIncome = userInputModel.getApplicantData().getWeeklyIncome('privatePension');

      if (userInputModel.isCouple() && numberUtils.isNumeric(privatePensionIncome)) {
        return privatePensionIncome / 2;
      }
      return 0;
    };

    this.getDisregardedCarersPremium = function (isEntitledToPremium, carersAllowance) {
      return 0;
    };

    function calculatePensionSavingsCreditDisregard(params) {
      var qualifyingIncome = params.qualifyingIncome;
      var maxDisregard = params.maxDisregard;

      // KERP-4879
      // Savings Credit disregard is only to be given to people born before 5-4-1953 (5th April 1953)
      var thresholdAge = moment('05/04/1953', "DD/MM/YYYY");
      var ageDiff = thresholdAge.diff(params.birthday, 'days'); // must be greater than 0 to qualify

      if (ageDiff <= 0 || !params.isPensioner || qualifyingIncome < params.incomeLowerBound) {
        return 0;

      } else if (qualifyingIncome >= params.incomeLowerBound && qualifyingIncome <= params.incomeUpperBound) {
        return params.pscAmount ? Math.min(params.pscAmount, maxDisregard) : maxDisregard;

      } else {
        return maxDisregard;
      }
    }

    this.getChildrensPremium = function () {
      return 0;
    };

    /**
     * The rules are described in the flow chart attached to this issue
     * https://cognovi.atlassian.net/browse/KERP-2664
     *
     * @returns {number}
     */
    this.getDisregardedPensionSavingsCredit = function (userType, results) {

      var partner = constants.userTypes.partner;
      var user = constants.userTypes.user;

      if (userType === partner) {
        return 0;
      }
      var applicantData = userInputModel.getApplicantData();
      var pscAmount = applicantData.getWeeklyIncome('pensionSavingsCredit');
      var isPensioner = results[user].ageLevel === constants.levels.high;
      var capitalTariff = results[user].capitalTariff;
      var qualifyingIncome;

      if (capitalTariff === constants.fullCost) {
        qualifyingIncome = 0;

      } else if (numberUtils.isNumeric(capitalTariff)) {
        qualifyingIncome = capitalTariff;

      } else {
        throw new Error('Capital tariff has not been calculated yet');
      }

      var pscDisregard = 0;

      var dateOfBirth = applicantData.dob;

      var birthday = moment(dateOfBirth, "DD/MM/YYYY");

      if (userInputModel.isCouple()) {
        var partnerData = userInputModel.getPartnerData();
        var partnerIsPensioner = results[partner].ageLevel === constants.levels.high;
        var excludedIncomes = ['attendanceAllowance', 'mobilityAllowance'];
        var qualifyingIncomeAccumulator = function (runningTotal, incomeSource) {
          return excludedIncomes.indexOf(incomeSource.name) > -1 ? runningTotal :
            runningTotal + incomeSource.weeklyAmount;
        };

        qualifyingIncome = applicantData.incomes.reduce(qualifyingIncomeAccumulator, qualifyingIncome);
        qualifyingIncome = partnerData.incomes.reduce(qualifyingIncomeAccumulator, qualifyingIncome);

        pscDisregard = calculatePensionSavingsCreditDisregard({
          maxDisregard: constants.pensionCreditAllowances.maxDisregardCouple,
          incomeLowerBound: constants.pensionCreditAllowances.coupleIncomeLowerBound,
          incomeUpperBound: constants.pensionCreditAllowances.coupleIncomeUpperBound,
          qualifyingIncome: qualifyingIncome,
          isPensioner: isPensioner || partnerIsPensioner,
          pscAmount: pscAmount,
          birthday: birthday
        });

      } else {
        qualifyingIncome += applicantData.getWeeklyIncome('privatePension') +
          applicantData.getWeeklyIncome('retirementPension');

        pscDisregard = calculatePensionSavingsCreditDisregard({
          maxDisregard: constants.pensionCreditAllowances.maxDisregardSingle,
          incomeLowerBound: constants.pensionCreditAllowances.singleIncomeLowerBound,
          incomeUpperBound: constants.pensionCreditAllowances.singleIncomeUpperBound,
          qualifyingIncome: qualifyingIncome,
          isPensioner: isPensioner,
          pscAmount: pscAmount,
          birthday: birthday
        });
      }

      return pscDisregard;
    };

    this.adjustContribution = function (contributionUpperBound, results) {
      return contributionUpperBound;
    };
  }

  return RespiteCareType;
}

RespiteCareTypeFactory.$inject = ['kirkleesCareAssessmentConstants', 'numberUtils', 'kirkleesCareAssessmentService', 'careAssessmentService'];

angular.module('kerp-forms.forms').factory('RespiteCareType', RespiteCareTypeFactory);
